// src/components/Navbar.tsx
import { Home, Users, Calendar, CreditCard, User, BarChart3, Shield, Search } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

interface NavbarProps {
  userRole?: 'customer' | 'trainer' | 'admin';
}

export function Navbar({ userRole }: NavbarProps) {
  const location = useLocation();

  const getTabColor = (tabId: string) => {
  switch (tabId) {
    case 'home':
      // Desert gold - warm and welcoming
      return 'bg-[#E6A756] text-[#5D4037]';
    case 'feed':
      // Sage green - community vibes
      return 'bg-[#9CAF88] text-[#FEFCF8]';
    case 'trainers':
      // Sunset orange - professional energy
      return 'bg-[#D2824C] text-[#FEFCF8]';
    case 'services':
      // Desert sage - service-oriented
      return 'bg-[#B8C5A6] text-[#5D4037]';
    case 'search':
      // Light sand - neutral search
      return 'bg-[#F5EFE7] text-[#5D4037]';
    case 'bookings':
      // Soft beige - calm scheduling
      return 'bg-[#F8F4EE] text-[#5D4037]';
    case 'profile':
      // Warm amber - personal space
      return 'bg-[#F4C07A] text-[#5D4037]';
    case 'trainer-dashboard':
      // Clay brown - professional authority
      return 'bg-[#A0673D] text-[#FEFCF8]';
    case 'admin-panel':
      // Warm brown - administrative control
      return 'bg-[#8B5A3C] text-[#FEFCF8]';
    default:
      // Default desert gold
      return 'bg-[#E6A756] text-[#5D4037]';
  }
};

  // Define tabs based on user role with their routes
  const getTabsForRole = () => {
    const baseTabs = [
      { id: 'home', label: 'Home', icon: Home, path: '/' },
      { id: 'feed', label: 'Feed', icon: Users, path: '/feed' },
      { id: 'trainers', label: 'Trainers', icon: Users, path: '/trainers' },
      { id: 'services', label: 'Services', icon: CreditCard, path: '/services' },
      { id: 'search', label: 'Search', icon: Search, path: '/search' },
      // Only show bookings tab for non-trainers (customers and admins)
      ...(userRole !== 'trainer' ? [{
        id: 'bookings',
        label: 'Bookings',
        icon: Calendar,
        path: '/bookings'
      }] : []),
      { id: 'profile', label: 'Profile', icon: User, path: '/profile' },
    ];

    // Add trainer dashboard if user is a trainer
    if (userRole === 'trainer') {
      baseTabs.splice(-1, 0, { // Insert before profile
        id: 'trainer-dashboard',
        label: 'Dashboard',
        icon: BarChart3,
        path: '/trainer-dashboard'
      });
    }

    // Add admin panel if user is an admin
    if (userRole === 'admin') {
      baseTabs.splice(-1, 0, { // Insert before profile
        id: 'admin-panel',
        label: 'Admin Panel',
        icon: Shield,
        path: '/admin-panel'
      });
    }

    return baseTabs;
  };

  const tabs = getTabsForRole();

  // Helper function to check if a tab is active
  const isTabActive = (tabPath: string, tabId: string) => {
    // Special case for home route
    if (tabPath === '/' && location.pathname === '/') {
      return true;
    }
    // For other routes, check if the current path starts with the tab path
    if (tabPath !== '/') {
      return location.pathname.startsWith(tabPath);
    }
    return false;
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-8">
            <NavLink to="/" className="flex items-center space-x-2 py-4">
              <div className="w-13 h-14 rounded-lg overflow-hidden">
                <img
                  src="Logo2.png"
                  alt="AZTrainers Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-xl font-bold text-gray-800">AZTrainers</span>
            </NavLink>
          </div>

          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = isTabActive(tab.path, tab.id);

              return (
                <NavLink
                  key={tab.id}
                  to={tab.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center space-x-2 ${isActive
                    ? getTabColor(tab.id)
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
}